<template>
  <DailyScheduleProvider :jobs="filteredJobs" :groupings="groupings" :now="now">
    <template #default="{ items, groups, options }">
      <div class="timeline">
        <DailyScheduleNavigator :range="range" @rangechanged="onRangeChanged" />

        <DailyScheduleTimeline
          ref="timeline"
          :items="items"
          :groups="groups"
          :options="options"
          :range="range"
          @select="onJobSelected"
          @rangechanged="onRangeChanged"
        />
      </div>
    </template>
  </DailyScheduleProvider>
</template>

<script>
import moment from 'moment'
import { orderBy } from 'lodash'
import { mapGetters, mapMutations, mapState } from 'vuex'
import DailyScheduleProvider from './DailyScheduleProvider'
import DailyScheduleTimeline from './DailyScheduleTimeline'
import DailyScheduleNavigator from './DailyScheduleNavigator'
import { SET_RANGE } from '@/store/modules/day-view'
import { mapState as mapPiniaState } from 'pinia'
import { useScheduleFilterStore } from '@/components/schedule/ScheduleFilterStore'
import { useFilteredJobStoreLegacyAdapter } from '../jobs/job-search-page/filteredJobStore'
import { useGetJobFilterOptions } from '@/api/filter-options'
import { until } from '@vueuse/core'
import { ref, onMounted } from 'vue'

export default {
  name: 'Schedule',
  components: {
    DailyScheduleTimeline,
    DailyScheduleProvider,
    DailyScheduleNavigator,
  },
  setup() {
    const { data: filterOptions, isFetched: isFilterOptionsFetched } =
      useGetJobFilterOptions()
    const store = useFilteredJobStoreLegacyAdapter()
    const defaultResourceTypeIds = ref([])
    const defaultDivisonIds = ref([])
    const defaultServiceProviderIds = ref([])

    onMounted(async () => {
      await until(isFilterOptionsFetched).toBe(true)

      defaultResourceTypeIds.value =
        filterOptions.value.jobFilterOptions.resourceTypeOptions.map(
          (d) => d.id
        ) ?? []

      defaultDivisonIds.value =
        filterOptions.value.jobFilterOptions.divisionOptions.map((d) => d.id) ??
        []

      defaultServiceProviderIds.value = [
        null,
        ...(filterOptions.value.jobFilterOptions.serviceProviderOptions.map(
          (d) => d.id
        ) ?? []),
      ]
    })

    return {
      filterOptions,
      isFilterOptionsFetched,
      store,
      defaultResourceTypeIds,
      defaultDivisonIds,
      defaultServiceProviderIds,
    }
  },
  data() {
    return {
      now: moment.utc(),
      interval: null,
      updateFrequency: moment.duration(30, 'seconds').asMilliseconds(),
    }
  },
  computed: {
    ...mapState('dayView', ['range']),
    ...mapGetters('jobs', { jobs: 'getNonArchivedJobs' }),
    ...mapGetters('unitNumbers', ['getVisibleUnits']),
    ...mapGetters('resourceTypes', ['getAllResourceTypes']),
    ...mapGetters('divisions', { getDivisionById: 'getById' }),
    ...mapGetters('companies', {
      getCompanyByOrganizationId: 'getByOrganizationId',
    }),
    ...mapGetters('permissions', ['getRoleAssignmentBusinessUnitIds']),
    ...mapPiniaState(useScheduleFilterStore, {
      divisionFiltersExplicitlySet: 'divisionFiltersExplicitlySet',
      resourceTypeFiltersExplicitlySet: 'resourceTypeFiltersExplicitlySet',
      serviceProviderFiltersExplicitlySet:
        'serviceProviderFiltersExplicitlySet',
      showOfflineUnits: 'showOfflineUnits',
      showStandbyUnits: 'showStandbyUnits',
      showUnitsWithNoAssignedJobs: 'showUnitsWithNoAssignedJobs',
    }),
    selectedDivisions() {
      if (
        !this.divisionFiltersExplicitlySet &&
        this.store.selectedDivisions.value.length === 0
      ) {
        return this.defaultDivisonIds
      }

      return this.store.selectedDivisions.value
    },
    selectedResourceTypes() {
      if (
        !this.resourceTypeFiltersExplicitlySet &&
        this.store.selectedResourceTypes.value.length === 0
      ) {
        return this.defaultResourceTypeIds
      }

      return this.store.selectedResourceTypes.value
    },
    selectedServiceProviders() {
      if (
        !this.serviceProviderFiltersExplicitlySet &&
        this.store.selectedServiceProviders.value.length === 0
      ) {
        return this.defaultServiceProviderIds
      }

      return this.store.selectedServiceProviders.value
    },

    groupings() {
      const units = Object.values(this.getVisibleUnits || {})
      const parentGroups = (this.getAllResourceTypes || []).filter((rt) =>
        this.selectedResourceTypes.includes(rt.id)
      )

      return parentGroups.map((group) => {
        const unitsForResourceType = units.filter(
          (u) => u.resourceTypeId === group.id
        )

        const orderedUnits = orderBy(this.filterUnits(unitsForResourceType), [
          'isOffline',
          'isActive',
          'isStandby',
          (r) => {
            const division =
              this.getDivisionById && typeof this.getDivisionById === 'function'
                ? this.getDivisionById(r.divisionId)
                : null
            return division?.name || ''
          },
          (r) => {
            const company =
              this.getCompanyByOrganizationId &&
              typeof this.getCompanyByOrganizationId === 'function'
                ? this.getCompanyByOrganizationId(r.organizationId)
                : null
            return company?.name || ''
          },
        ])

        return {
          id: `resourceTypeId-${group.id}`,
          group,
          units: orderedUnits,
          unitsOverview: this.unitsOverview(
            orderedUnits,
            unitsForResourceType.length
          ),
        }
      })
    },

    filteredJobs() {
      const filtered = this.jobs
        .filter((j) => this.selectedDivisions.includes(j.ownerId))
        .filter((j) => this.selectedResourceTypes.includes(j.resourceTypeId))
        .filter((j) => j.isResourceAssigned)

      return orderBy(filtered, [
        'isLate',
        'isOverdue',
        'activeTime',
        'startTime',
      ])
    },
  },
  mounted() {
    let frame
    this.interval = setInterval(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        this.now = moment.utc()
      })
    }, this.updateFrequency)
  },
  beforeDestroy() {
    clearInterval(this.interval)
  },
  methods: {
    ...mapMutations('dayView', {
      setRange: SET_RANGE,
    }),
    onJobSelected({ items }) {
      if (!items?.length) return

      const [jobItemId] = items
      const [, id] = jobItemId.match(/job-(\d+)/)
      this.$emit('select', id)
    },
    onRangeChanged({ start, end }) {
      this.setRange({ start: moment(start), end: moment(end) })
    },

    filterUnits(units) {
      if (!Array.isArray(units)) return []

      return units.filter((unit) => {
        if (!this.showOfflineUnits && unit.isOffline) return false
        if (!this.showStandbyUnits && unit.isStandby) return false
        if (
          !this.showUnitsWithNoAssignedJobs &&
          !this.jobs.some((j) => j.unitId === unit.id)
        )
          return false

        if (!this.selectedDivisions.length) return false

        const division =
          this.getDivisionById && typeof this.getDivisionById === 'function'
            ? this.getDivisionById(unit.divisionId)
            : null

        const roleAssignmentIds = Array.isArray(
          this.getRoleAssignmentBusinessUnitIds
        )
          ? this.getRoleAssignmentBusinessUnitIds
          : []

        const divisionMatch =
          this.selectedDivisions.some((id) => id === division?.uuid) ||
          (unit.divisionId === null &&
            roleAssignmentIds.some((b) => b === unit.ownerId))

        if (!divisionMatch) return false

        if (!this.selectedServiceProviders.length) return false

        return this.selectedServiceProviders.some(
          (id) => id === unit.organizationId
        )
      })
    },

    unitsOverview(filteredUnits, allUnitsCount) {
      if (allUnitsCount === 0) return 'No resources in this group'

      const counts = []

      const activeCount = filteredUnits.filter((unit) => unit.isActive).length
      if (activeCount > 0) counts.push(`${activeCount} active`)

      if (this.showStandbyUnits) {
        const standbyCount = filteredUnits.filter(
          (unit) => unit.isStandby
        ).length
        if (standbyCount > 0) counts.push(`${standbyCount} standby`)
      }

      if (this.showOfflineUnits) {
        const offlineCount = filteredUnits.filter(
          (unit) => unit.isOffline
        ).length
        if (offlineCount > 0) counts.push(`${offlineCount} offline`)
      }

      const hiddenCount = allUnitsCount - filteredUnits.length
      if (hiddenCount > 0) counts.push(`${hiddenCount} hidden`)

      return counts.join(', ') || 'No units visible'
    },
  },
}
</script>

<style scoped>
.timeline {
  position: relative;
  height: 100%;
}
</style>
